import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { authService, clearAuthToken, setRefreshToken } from "../services/authService";

const STORAGE_KEYS = {
  user: "user",
  token: "token",
} as const;

type UserRole = "admin" | "user";

/**
 * Decode a JWT and return its payload without an external library.
 * Returns null if the token is malformed.
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padding = "=".repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(payload + padding));
  } catch {
    return null;
  }
}

/** Returns true if the JWT has already expired (or can't be decoded). */
function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return true;
  // `exp` is in seconds; Date.now() is in milliseconds
  return Date.now() >= payload.exp * 1000;
}

export interface UserAddress {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface UserPreferences {
  newsletter?: boolean;
  notifications?: boolean;
  language?: string;
  theme?: string;
}

export interface UserSubscription {
  plan: string;
  status: string;
  billingCycle: string;
  endDate?: string;
  price?: number;
  currency?: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  type: UserRole;
  phone?: string;
  avatar?: string;
  bio?: string;
  dateOfBirth?: string;
  address?: UserAddress;
  preferences?: UserPreferences;
  isActive?: boolean;
  step1Completed?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  token?: string;
  refreshToken?: string;
  subscription?: UserSubscription | null;
}

function normalizeUserRole(role: unknown): UserRole {
  return role === "admin" ? "admin" : "user";
}

function isValidStoredUser(value: unknown): value is Omit<User, "token" | "refreshToken"> {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybeUser = value as Partial<User>;
  return (
    typeof maybeUser._id === "string" &&
    typeof maybeUser.name === "string" &&
    typeof maybeUser.email === "string"
  );
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => Promise<void>;
  updateUser: (data: Partial<User>) => void;
  isAuthenticated: boolean;
  isAuthReady: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const logout = useCallback(async () => {
    // Best-effort server-side token revocation
    try {
      await authService.logout();
    } catch {
      // Server call failed — still clear local state
      clearAuthToken();
    }
    setUser(null);
  }, []);

  useEffect(() => {
    // Check for stored user data on app load
    const storedUser = localStorage.getItem(STORAGE_KEYS.user);
    const storedToken = localStorage.getItem(STORAGE_KEYS.token);

    if (storedUser && storedToken) {
      // Reject expired tokens immediately so the user is not
      // silently treated as authenticated with a dead session.
      if (isTokenExpired(storedToken)) {
        clearAuthToken();
        setIsAuthReady(true);
        return;
      }
      try {
        const parsedUser = JSON.parse(storedUser);
        if (!isValidStoredUser(parsedUser)) {
          clearAuthToken();
          setIsAuthReady(true);
          return;
        }

        setUser({
          ...parsedUser,
          type: normalizeUserRole(parsedUser.type),
          token: storedToken,
        });
      } catch (error) {
        console.error("Error parsing stored user data:", error);
        clearAuthToken();
      }
    }

    setIsAuthReady(true);
  }, []);

  // Listen for the global session-expired event fired by the axios
  // response interceptor whenever a refresh attempt fails.
  useEffect(() => {
    const handleSessionExpired = () => {
      setUser(null);
      // Redirect to login; use replace so Back doesn't return to the
      // protected page with a broken session.
      window.location.replace("/login?reason=session_expired");
    };

    window.addEventListener("auth:sessionExpired", handleSessionExpired);
    return () => {
      window.removeEventListener("auth:sessionExpired", handleSessionExpired);
    };
  }, []);

  const login = (userData: User) => {
    // Store refresh token separately if provided
    if (userData.refreshToken) {
      setRefreshToken(userData.refreshToken);
    }
    // Strip the refreshToken from the user object stored in localStorage
    const { refreshToken: _rt, token: _at, ...userToStore } = userData;
    const normalizedUser: User = {
      ...userToStore,
      type: normalizeUserRole(userToStore.type),
      token: userData.token,
    };
    setUser(normalizedUser);
    localStorage.setItem(STORAGE_KEYS.user, JSON.stringify({ ...userToStore, type: normalizedUser.type }));
    if (userData.token) {
      localStorage.setItem(STORAGE_KEYS.token, userData.token);
    }
  };

  const updateUser = (data: Partial<User>) => {
    if (user) {
      const updatedUser = {
        ...user,
        ...data,
        type: normalizeUserRole((data.type ?? user.type) as unknown),
      };
      setUser(updatedUser);
      const { token: _at, refreshToken: _rt, ...persistedUser } = updatedUser;
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(persistedUser));
    }
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, isAuthenticated, isAuthReady }}>
      {children}
    </AuthContext.Provider>
  );
};
